import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { isEqual } from 'lodash'
import { TextField } from '@mui/material'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader } from '@carto/meridian-ds/components'
import { Map } from 'features/maps/types'
import { useEditMap } from 'features/maps/hooks/mapsHooks'
import MapEditDialogDescription from './MapEditDialogDescription'
import TagsAutocomplete from 'features/tags/ui/TagsAutocomplete'
import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'
import EditDialogConfirmation from 'features/common/ui/EditDialogConfirmation'

interface MapEditDialogProps {
  map: Map
  open: boolean
  onClose: (edit?: boolean) => void
}

function MapEditDialog({ map, open, onClose }: MapEditDialogProps) {
  const form = useForm({ mode: 'onChange' })
  const intl = useIntl()
  const hasTagsForResourcesEnabled = useFeatureFlag(FeatureFlag.TagsForResources)
  const editMapMutation = useEditMap()

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [selectedTags, setSelectedTags] = useState(map.tags || [])
  const [description, setDescription] = useState<string | undefined>(map.description)

  const { register, handleSubmit, formState } = form
  const loading = editMapMutation.isLoading
  const canSave = description !== map.description || !isEqual(selectedTags, map.tags) || formState.isDirty

  const handleClose = () => {
    if (canSave && !showConfirmation) {
      setShowConfirmation(true)
    } else {
      onClose()
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (params: any) => {
    const editMap = { ...params, id: map.id, tags: selectedTags }
    editMapMutation.mutate(editMap)
  }

  useEffect(() => {
    if (editMapMutation.isSuccess) {
      onClose(true)
    }
  }, [editMapMutation.isSuccess, onClose])

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dialog
        size="medium"
        open={open}
        aria-labelledby="users-delete-form-dialog-title"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogHeader
          id="users-delete-form-dialog-title"
          title={<FormattedMessage id="maps.edit.title" />}
          onClose={handleClose}
        />
        <DialogContent>
          <TextField
            name="title"
            size="small"
            inputProps={{ 'aria-label': 'name', ...register('title', { required: true }) }}
            defaultValue={map.title}
            placeholder={intl.formatMessage({ id: 'maps.edit.inputs.name' })}
            label={intl.formatMessage({ id: 'maps.edit.inputs.name' })}
          />
          {hasTagsForResourcesEnabled && (
            <TagsAutocomplete selectedTags={selectedTags} onChange={(tags) => setSelectedTags(tags)} />
          )}
          <MapEditDialogDescription
            form={form}
            description={description}
            onChange={(value) => {
              setDescription(value)
              form.setValue('description', value, { shouldValidate: true })
            }}
          />
          {showConfirmation && <EditDialogConfirmation onClose={onClose} onCancel={() => setShowConfirmation(false)} />}
        </DialogContent>
        <DialogFooter>
          <Button disabled={!canSave} variant="contained" loading={loading} type="submit">
            <FormattedMessage id="maps.edit.buttons.save" />
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  )
}

export default MapEditDialog
